import cx from 'classnames';
import type { VoidFunctionComponent } from 'react';

import { RadioGroup } from '@mui/material';
import { useOrganizationPackageLabels } from '../../../hooks/api/packageLabels';
import type { SelectOption } from '../../../types';
import { LabeledRadio, Select, icons } from '../../generic';
import styles from './PackageLabelOptions.module.less';
import TooltipIcon from '../../generic/TooltipIcon';

type PackageLabelsEnabledRadio = 'always' | 'enabled';

type PackageLabelOptionsProps = {
  isEnabled: boolean;
  setIsEnabled: (isEnabled: boolean) => void;
  packageLabels: SelectOption[];
  setPackageLabels: (labels: SelectOption[]) => void;
  isRequired?: boolean;
};

const PackageLabelOptions: VoidFunctionComponent<PackageLabelOptionsProps> = ({
  isEnabled,
  setIsEnabled,
  packageLabels,
  setPackageLabels,
  isRequired,
}) => {
  const { data: labels, isLoading: isLoadingLabels } = useOrganizationPackageLabels();
  const onToggle = (value: PackageLabelsEnabledRadio) => {
    setIsEnabled(value === 'enabled');
    if (value === 'always') {
      setPackageLabels([]);
    }
  };

  const alwaysLabel = <span className={styles.packageLabelRadioLabel}>Always</span>;

  const enabledLabelText = isRequired
    ? 'If the dependency has Package Labels assigned (Required)'
    : 'If the dependency has Package Labels assigned';

  const enabledLabel = (
    <span className={styles.packageLabelRadioLabel}>
      {enabledLabelText}
      {!isRequired && (
        <TooltipIcon
          tooltip="If the selected issues do not have these labels, they will not be ignored but a rule will still be created."
          icon={icons.circleQuestion}
          id="package-label-selection-tooltip"
        />
      )}
    </span>
  );

  const arePackageLabelsChosen = isRequired ? true : isEnabled;
  return (
    <>
      <RadioGroup
        value={arePackageLabelsChosen ? 'enabled' : 'always'}
        onChange={(e) => onToggle(e.target.value as PackageLabelsEnabledRadio)}
        className={cx(styles.packageLabelRadios, 'radio-filter-group')}
      >
        <div className="radio-filter-group-checks">
          <LabeledRadio label={alwaysLabel} value="always" isDisabled={isRequired} />
          <LabeledRadio label={enabledLabel} value="enabled" />
        </div>
      </RadioGroup>
      {arePackageLabelsChosen && !isLoadingLabels && (
        <Select
          id="issue-exception-modal-package-labels-select"
          isMulti
          placeholder="Select package labels..."
          value={packageLabels}
          onChange={(options) => setPackageLabels(options)}
          options={(labels?.packageLabels || []).map((label) => ({
            label: label.name,
            value: label.id.toString(),
          }))}
          className={styles.packageLabelOptionsSelect}
        />
      )}
    </>
  );
};

export default PackageLabelOptions;
